import type { MomentsMemberArtifactSummary } from '@/store/api/momentsApi';

import { CHEM_CALL_REFLECTION_MEMBER_PROFILE_SLUG } from '@/components/Sessions/memberReflectionProfiles';
import {
  parseMarkdownBlocks,
  parseToVisibleSegments,
} from '@/pages/MemberReflectionArtifact/reflectionUtils';
import { parseDisplayDate } from '@/utils/dateSlug';

export interface NextSessionPrepHomeCard {
  artifactId: string;
  dateLabel: string;
  href: string;
  id: string;
  relativeLabel: string;
  sessionId?: string;
  sortTime: number;
  title: string;
}

export interface HomeSessionAssetRow {
  contextLabel?: string;
  dateLabel: string;
  href: string;
  id: string;
  savedHighlightCount: number;
  sessionId?: string;
  snippet: string;
}

const chemistryCallLabel = 'Chemistry call';
const fallbackSnippet = 'View your session reflection.';
const chemistryFallbackSnippet = 'View your chemistry call reflection.';
const dateSlugPattern = /^(\d{4}-\d{2}-\d{2})/u;
const dottedDatePattern = /\d{4}\.\d{2}\.\d{2}/u;

const dateOptions: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
};
const dateWithYearOptions: Intl.DateTimeFormatOptions = {
  ...dateOptions,
  year: 'numeric',
};

/**
 * Short session date, with the year appended only when the session falls outside
 * the year it is being viewed from. Shared by the reflection rows and the
 * next-session topics card so a Dec/Jan boundary cannot read as the current year
 * on one surface and not the other.
 *
 * `now` is a parameter rather than a `Date.now()` call so the comparison is
 * testable, and so it can reuse the `now` the prep mapping already threads
 * through for expiry.
 */
export const formatShortSessionDate = (date: Date, now: number): string =>
  date.toLocaleDateString(
    'en-US',
    date.getFullYear() === new Date(now).getFullYear()
      ? dateOptions
      : dateWithYearOptions,
  );

export const formatSessionReflectionDate = (
  value: string | undefined,
  now = Date.now(),
): string => {
  if (!value) {
    return '';
  }

  const date = parseDisplayDate(value);
  return date ? formatShortSessionDate(date, now) : '';
};

export const getSessionReflectionSortTime = (
  artifact: MomentsMemberArtifactSummary,
): number => {
  const value = artifact.sessions[0]?.plannedStartAt || artifact.updatedAt;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
};

const getDateSlugFromValue = (value: string, fallback: string): string => {
  const sourceDateSlug = value.match(dateSlugPattern)?.[1];
  if (sourceDateSlug) {
    return sourceDateSlug;
  }

  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().slice(0, 10);
  }

  return fallback;
};

export const getSessionReflectionPath = (
  artifact: MomentsMemberArtifactSummary,
): string => {
  const sourceValue =
    artifact.sessions[0]?.plannedStartAt || artifact.updatedAt;
  const dateSlug = getDateSlugFromValue(sourceValue, 'reflection');

  return `/m/artifacts/session-reflection/${artifact.artifactId}/${dateSlug}`;
};

export const getSessionReflectionTitle = (
  artifact: MomentsMemberArtifactSummary,
  dateLabel: string,
): string => {
  const title = artifact.title.trim();
  if (!title) {
    return 'Session reflection';
  }

  const dateSlug = (
    artifact.sessions[0]?.plannedStartAt || artifact.updatedAt
  ).match(dateSlugPattern)?.[1];
  const withoutDate = title
    .replace(dateLabel, '')
    .replace(dateSlug ?? '', '')
    .replace(dottedDatePattern, '')
    .replace(/^[\s:–—-]+|[\s:–—-]+$/gu, '')
    .replace(/\s{2,}/gu, ' ')
    .trim();

  return withoutDate || 'Session reflection';
};

export const getSessionReflectionSnippet = (
  markdown: string | undefined,
  profileSlug?: string,
): string => {
  const fallback =
    profileSlug === CHEM_CALL_REFLECTION_MEMBER_PROFILE_SLUG
      ? chemistryFallbackSnippet
      : fallbackSnippet;
  if (!markdown?.trim()) {
    return fallback;
  }

  const blocks = parseMarkdownBlocks(markdown);
  const block =
    blocks.find(({ text, type }) => type !== 'heading' && text.trim()) ??
    blocks.find(({ text }) => text.trim());
  if (!block) {
    return fallback;
  }

  const visibleText = parseToVisibleSegments(block.text)
    .map((segment) => segment.text)
    .join('')
    .replace(/\s+/gu, ' ')
    .trim();

  if (!visibleText) {
    return fallback;
  }

  return visibleText;
};

export const getSessionPrepPath = (
  artifact: MomentsMemberArtifactSummary,
): string => {
  const sourceValue =
    artifact.sessions[0]?.plannedStartAt || artifact.updatedAt || '';
  const dateSlug = getDateSlugFromValue(sourceValue, 'prep');

  return `/m/artifacts/session-prep/${artifact.artifactId}/${dateSlug}`;
};

const startOfLocalDay = (value: number) => {
  const date = new Date(value);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
};

const formatSessionPrepRelativeLabel = (sessionDate: Date, now: number) => {
  const diffDays = Math.round(
    (startOfLocalDay(sessionDate.getTime()) - startOfLocalDay(now)) /
      (24 * 60 * 60 * 1000),
  );
  if (diffDays <= 0) {
    return 'Today';
  }
  if (diffDays === 1) {
    return 'Tomorrow';
  }
  return `In ${diffDays} days`;
};

export const getSessionPrepSortTime = (prep: NextSessionPrepHomeCard): number =>
  prep.sortTime;

export const mapArtifactToNextSessionPrep = (
  artifact: MomentsMemberArtifactSummary,
  now = Date.now(),
): NextSessionPrepHomeCard | null => {
  const targetSession = artifact.sessions[0];
  const plannedStartAt = targetSession?.plannedStartAt;
  if (!plannedStartAt) {
    return null;
  }

  const start = parseDisplayDate(plannedStartAt);
  if (!start) {
    return null;
  }

  if (targetSession.plannedEndAt) {
    const end = new Date(targetSession.plannedEndAt);
    if (!Number.isNaN(end.getTime()) && end.getTime() <= now) {
      return null;
    }
  } else if (start.getTime() <= now) {
    return null;
  }

  const weekday = start.toLocaleDateString('en-US', { weekday: 'long' });

  return {
    artifactId: artifact.artifactId,
    dateLabel: formatShortSessionDate(start, now),
    href: getSessionPrepPath(artifact),
    id: artifact.artifactId,
    relativeLabel: formatSessionPrepRelativeLabel(start, now),
    sessionId:
      targetSession.sessionId || targetSession.legacySessionId || undefined,
    sortTime: start.getTime(),
    title: `Ideas for where to start on ${weekday}`,
  };
};

const getSessionReflectionContextLabel = (
  artifact: MomentsMemberArtifactSummary,
): string | undefined => {
  if (artifact.profileSlug !== CHEM_CALL_REFLECTION_MEMBER_PROFILE_SLUG) {
    return undefined;
  }

  const coachName =
    artifact.sessions.length === 1
      ? artifact.sessions[0]?.coachName?.trim()
      : undefined;

  return coachName
    ? `${chemistryCallLabel} with ${coachName}`
    : chemistryCallLabel;
};

export const mapArtifactToSessionReflectionRow = (
  artifact: MomentsMemberArtifactSummary,
  savedHighlightCount = 0,
): HomeSessionAssetRow => {
  const dateLabel = formatSessionReflectionDate(
    artifact.sessions[0]?.plannedStartAt || artifact.updatedAt,
  );

  return {
    contextLabel: getSessionReflectionContextLabel(artifact),
    dateLabel,
    href: getSessionReflectionPath(artifact),
    id: artifact.artifactId,
    savedHighlightCount,
    sessionId:
      artifact.sessions[0]?.sessionId ||
      artifact.sessions[0]?.legacySessionId ||
      undefined,
    snippet: getSessionReflectionSnippet(
      artifact.markdown,
      artifact.profileSlug,
    ),
  };
};
